import { useState } from "react";
import type { FormEvent } from "react";

type Status = "idle" | "loading" | "success" | "error";

const FORM_URL = "https://readdy.ai/api/form/dbadjvo0gghjvf9i9img";

interface FeedbackFormProps {
  onSubmitted?: (ok: boolean) => void;
}

export default function FeedbackForm({ onSubmitted }: FeedbackFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honey = String(payload.get("company_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已收到这条消息。");
      form.reset();
      onSubmitted?.(true);
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        if (key === "company_alt") return;
        body.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const succeeded = response.ok && parsed?.code === "OK";

      if (!succeeded || String(serverMsg).toLowerCase().includes("spam")) {
        setStatus("error");
        setMessage(serverMsg && !String(serverMsg).toLowerCase().includes("spam") ? serverMsg : "提交未能完成，请稍后重试。");
        onSubmitted?.(false);
        return;
      }

      setStatus("success");
      setMessage("感谢你的反馈，我们已收到这条消息，会尽快整理核实。");
      form.reset();
      onSubmitted?.(true);
    } catch {
      setStatus("error");
      setMessage("网络连接出现问题，暂时无法提交，请稍后再试。");
      onSubmitted?.(false);
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-semibold text-foreground-950">意见反馈</h3>
      <p className="mt-2 text-xs leading-6 text-foreground-600">
        发现资料有误、想补充内容，或只是想聊聊这个站点，都可以在这里留言。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-700">
            你的称呼 <span className="text-primary-600">*</span>
          </span>
          <input
            type="text"
            name="name"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-700">
            邮箱 <span className="text-primary-600">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            maxLength={80}
            placeholder="用于回复你的反馈"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-700">反馈类型</span>
        <select
          name="type"
          defaultValue="资料补充"
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="资料补充">资料补充</option>
          <option value="内容纠错">内容纠错</option>
          <option value="页面建议">页面建议</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-700">
          留言内容 <span className="text-primary-600">*</span>
        </span>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简单描述你遇到的问题或建议（500 字以内）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </label>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-field-alt"
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-5 text-foreground-500">
          我们只会使用你的邮箱回复本次反馈，不会用于其它用途。
        </p>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
              提交中
            </>
          ) : (
            <>
              提交反馈
              <i className="ri-send-plane-line" aria-hidden="true"></i>
            </>
          )}
        </button>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-xs leading-5 text-primary-800">
          <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs leading-5 text-accent-900">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          {message}
        </p>
      )}
    </form>
  );
}